import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, CheckCircle2, Lock } from "lucide-react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";
import { apiRequest, queryClient } from "@/lib/queryClient";

const MUTED = "#94a3b8";
const INDIGO = "#6366f1";
const BORDER = "rgba(99,102,241,0.18)";

export default function Login() {
  const [, setLocation] = useLocation();

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [pwEmail, setPwEmail] = useState("");
  const [pwPassword, setPwPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");

  const returnTo = new URLSearchParams(window.location.search).get("returnTo") || "";

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setIsLoading(true);
    try {
      const body: Record<string, string> = { email };
      if (returnTo) body.returnTo = returnTo;
      const res = await fetch("/api/auth/request-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSent(true);
      } else {
        setErrorMsg("Something went wrong. Please try again.");
      }
    } catch {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError("");
    setPwLoading(true);
    try {
      const res = await apiRequest("POST", "/api/auth/login", { email: pwEmail, password: pwPassword });
      const data = await res.json();
      const role = data.user?.role;
      queryClient.clear();
      if (role === "ADMIN") {
        window.location.href = "/mother";
      } else {
        window.location.href = returnTo || "/agent/dashboard";
      }
    } catch (err: any) {
      const msg = err?.message || "";
      if (msg.includes("401")) {
        setPwError("Invalid email or password");
      } else {
        setPwError("Something went wrong. Please try again.");
      }
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#05070a" }}>
      <div className="w-full max-w-xl">
        <div className="text-center mb-10">
          <Link href="/">
            <img src={vexisLogo} alt="VEXIS" className="h-14 w-auto mx-auto cursor-pointer" />
          </Link>
          <h2 className="text-3xl font-bold text-white mt-7">Sign in to Vexis</h2>
          <p className="text-base mt-2" style={{ color: MUTED }}>Enter the email address you registered with.</p>
        </div>

        <div className="rounded-2xl p-10" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}` }}>
          {sent ? (
            <div className="text-center space-y-4 py-2">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: "rgba(34,197,94,0.15)" }}>
                <CheckCircle2 className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Check your email</h3>
              <p className="text-sm" style={{ color: MUTED }}>
                If this email is registered, you'll receive a login link shortly.
              </p>
              <button onClick={() => { setSent(false); setEmail(""); }}
                className="text-xs underline text-indigo-400 hover:text-indigo-300">
                Try a different email
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={handleMagicLink} className="space-y-6">
                {errorMsg && (
                  <div className="rounded-lg px-4 py-3 text-sm text-red-300" style={{ backgroundColor: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)" }}>
                    {errorMsg}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white text-base">Work email</Label>
                  <Input id="email" type="email" required placeholder="jane@acmerecruitment.com"
                    value={email} onChange={e => setEmail(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500 h-12 text-base"
                    data-testid="input-email" />
                </div>
                <Button type="submit" className="w-full h-14 font-semibold text-white text-base"
                  style={{ backgroundColor: INDIGO }} disabled={isLoading} data-testid="button-send-link">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Mail className="w-5 h-5 mr-2" />}
                  {isLoading ? "Sending..." : "Send me a login link"}
                </Button>
              </form>

              <div className="my-6 flex items-center gap-4">
                <div className="flex-1 h-px" style={{ backgroundColor: BORDER }} />
                <span className="text-xs" style={{ color: MUTED }}>or sign in with password</span>
                <div className="flex-1 h-px" style={{ backgroundColor: BORDER }} />
              </div>

              <form onSubmit={handlePasswordLogin} className="space-y-4">
                {pwError && (
                  <div className="rounded-lg px-4 py-3 text-sm text-red-300" style={{ backgroundColor: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)" }}>
                    {pwError}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="pw-email" className="text-white text-sm">Email</Label>
                  <Input id="pw-email" type="email" required placeholder="jane@acmerecruitment.com"
                    value={pwEmail} onChange={e => setPwEmail(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500 h-11"
                    data-testid="input-pw-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pw-password" className="text-white text-sm">Password</Label>
                  <Input id="pw-password" type="password" required placeholder="••••••••"
                    value={pwPassword} onChange={e => setPwPassword(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500 h-11"
                    data-testid="input-pw-password" />
                </div>
                <Button type="submit" variant="outline" className="w-full h-12 font-semibold text-white text-sm border-white/10 hover:bg-white/5"
                  disabled={pwLoading} data-testid="button-password-login">
                  {pwLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  {pwLoading ? "Signing in..." : "Sign in with password"}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="mt-6 text-center space-y-2">
          <p className="text-sm" style={{ color: MUTED }}>
            New to Vexis?{" "}
            <Link href="/register" className="text-indigo-400 hover:text-indigo-300 underline">
              Get started →
            </Link>
          </p>
          <p className="text-xs" style={{ color: MUTED }}>
            Candidates receive assessment links directly — no login needed.
          </p>
        </div>
      </div>
    </div>
  );
}
